import { DIRECTION_VECTORS, Direction, chebyshevDistance } from '@game/protocol';

/**
 * Caminho até o tile em que o jogador clicou (PRD §6).
 *
 * Sem nenhuma dependência do Phaser, pelo mesmo motivo do `DirectionState`: a decisão de
 * "por onde ir" é a parte que erra em silêncio, e ela se testa sem navegador.
 *
 * ## O cliente traça, o servidor continua mandando
 *
 * Não há pacote de "ande até ali", e não deve haver: o servidor já valida passo a passo
 * (`World.canOccupy`), e um caminho inteiro chegando pronto do cliente seria um segundo
 * caminho de movimento sem as validações do primeiro — exatamente o que §14.1 proíbe. O que
 * este módulo faz é traduzir um clique numa fila de `Move 0x01`, um por passo, entregues ao
 * mesmo `pollInput` que o teclado alimenta. Para o servidor, andar por clique e andar por
 * WASD são indistinguíveis.
 *
 * ## Busca em largura, e não A\*
 *
 * O servidor usa A\* porque replaneja centenas de vezes por segundo (`map/pathfinding.ts`);
 * aqui a busca roda **uma vez por clique**, e nesse orçamento a largura é a escolha certa:
 * com custo unitário ela devolve o mesmo caminho mínimo em um terço do código e sem heap,
 * sem carimbo de geração e sem vetores de trabalho reaproveitados. O teto de expansão é o que
 * a torna segura — sem ele, um clique num pátio fechado varreria toda a região conhecida.
 *
 * ## O que o cliente conhece é menor que o mundo
 *
 * O terreno chega por chunk conforme o jogador se aproxima (PRD §3.5), e o que ainda não
 * chegou é `Unknown`, que `Terrain.isWalkable` recusa. Um clique além da fronteira do que já
 * veio não acha caminho — a resposta honesta, e a mesma que o jogador teria se clicasse numa
 * parede.
 */

/**
 * Teto de tiles expandidos por busca.
 *
 * Quem dimensiona é o **minimapa**, não a tela. Um clique nele alcança 32 tiles em cada
 * eixo (`ui/minimap.ts`), então o destino mais distante está a 64 passos — e uma busca em
 * largura que chega a 64 passos já visitou o losango inteiro de raio 64, que são 8.321
 * tiles. Os 4.096 de quando só se clicava no chão cobriam a tela com folga e recusavam,
 * silenciosamente e como se não houvesse caminho, metade do mapa que agora se clica.
 *
 * 32.768 é esse losango com margem para o desvio: dá conta de qualquer destino a até ~127
 * passos de caminhada real, o suficiente para contornar um lago entre os dois pontos.
 * Estourá-lo devolve `null`, que quem chama trata como "não sei chegar aí".
 *
 * O custo é uma busca por clique, e só chega perto do teto quando o destino é um tile de
 * chão de verdade que está cercado — clique em parede, em água ou no que ainda não chegou
 * do servidor sai na primeira linha de `findPath`, sem expandir nada.
 */
export const MAX_PATH_NODES = 32768;

/** Chave de tile num `Map`. `x` e `y` são `u16` no protocolo, então cabem os dois. */
function keyOf(x: number, y: number): number {
  return x * 0x10000 + y;
}

/**
 * Passos de (startX, startY) até (goalX, goalY), na ordem em que devem ser dados.
 *
 * `null` quando não há caminho conhecido ou quando o teto de expansão estourou. Um clique no
 * tile em que já se está devolve a lista vazia — que não é a mesma coisa que `null`: um é
 * "chegou", o outro é "não dá".
 *
 * `isFree` responde por terreno **e** ocupação, como no resto do cliente (ver `isOccupied` em
 * `net/prediction.ts`). O tile de destino não tem exceção: quem clica em cima de um rato quis
 * atacá-lo, e esse gesto é tratado antes de chegar aqui.
 */
export function findPath(
  startX: number,
  startY: number,
  goalX: number,
  goalY: number,
  isFree: (x: number, y: number) => boolean,
  maxNodes = MAX_PATH_NODES,
): Direction[] | null {
  if (startX === goalX && startY === goalY) return [];
  // O destino precisa ser pisável **antes** da busca: a largura só expande tile livre, então
  // sem esta linha um clique em parede varreria o mapa inteiro para descobrir que não chega.
  if (!isFree(goalX, goalY)) return null;

  return search(startX, startY, (x, y) => x === goalX && y === goalY, isFree, maxNodes);
}

/**
 * Teto de expansão da perseguição. Ver `findPathNear`.
 *
 * Muito menor que o do clique, e por uma razão de frequência: o clique é um gesto por vez e
 * pode pagar uma busca cara, enquanto a perseguição replaneja **a cada passo** — umas sete
 * vezes por segundo. O alvo perseguido está sempre dentro da Área de Interesse, a alguns
 * tiles de distância; o caso caro não é ele estar longe, é ele estar **inalcançável** —
 * atrás de uma parede, do outro lado de um lago —, e aí a busca gastaria o teto inteiro em
 * todo passo para concluir a mesma coisa.
 *
 * 1024 tiles é o losango de raio 22, com folga sobre a tela e sobre a AOI. O que estoura o
 * teto é tratado como "não sei chegar aí", que na perseguição significa parar de andar — e
 * parar é a resposta certa para um alvo que não se alcança a pé.
 */
export const MAX_CHASE_NODES = 1024;

/**
 * Passos até **chegar perto o bastante** de (goalX, goalY) — a perseguição da postura
 * ofensiva (PRD §5, ver `CombatStance`).
 *
 * A diferença para `findPath` é o critério de parada, e ela é o motivo desta função existir:
 * o destino de uma perseguição é uma **criatura**, e o tile dela está ocupado por definição.
 * Pedir `findPath` até lá devolveria `null` sempre — o alvo nunca é um tile livre.
 *
 * `range` é o alcance da arma em distância de Chebyshev, a mesma métrica que o servidor usa
 * para decidir se o golpe sai (ver `updateCombat`). Com ela o Knight para colado no alvo e o
 * Paladin para a três tiles, cada um no lugar de onde acerta — e não o mesmo lugar para os
 * dois, que faria a vocação de distância brigar de perto sem ter pedido.
 *
 * A lista vazia significa "já está no alcance, não precisa andar"; `null` significa "não sei
 * chegar aí". A distinção é a mesma de `findPath`, e importa pelo mesmo motivo: um é chegou,
 * o outro é não dá.
 */
export function findPathNear(
  startX: number,
  startY: number,
  goalX: number,
  goalY: number,
  range: number,
  isFree: (x: number, y: number) => boolean,
  maxNodes = MAX_CHASE_NODES,
): Direction[] | null {
  const reached = (x: number, y: number): boolean =>
    chebyshevDistance(x, y, goalX, goalY) <= range;

  if (reached(startX, startY)) return [];

  return search(startX, startY, reached, isFree, maxNodes);
}

/**
 * A busca em largura de que as duas saídas acima são casos particulares.
 *
 * O que muda entre elas é uma pergunta — "cheguei?" — e nada mais: mesma fila com cursor,
 * mesmo teto de expansão, mesma reconstrução. Duas cópias divergiriam na primeira correção
 * que alguém fizesse em uma delas.
 *
 * O tile de partida **não** passa por `isFree`: o personagem está em cima dele, e uma
 * criatura que acabou de entrar ali (ou o próprio cliente contando a si mesmo) faria a busca
 * recusar o lugar de onde ela começa.
 */
function search(
  startX: number,
  startY: number,
  reached: (x: number, y: number) => boolean,
  isFree: (x: number, y: number) => boolean,
  maxNodes: number,
): Direction[] | null {
  // De onde se chegou a cada tile, e por qual direção. A origem entra com direção nula para
  // a reconstrução saber onde parar.
  const cameFrom = new Map<number, { key: number; direction: Direction } | null>();
  cameFrom.set(keyOf(startX, startY), null);

  // Fila com cursor em vez de `shift()`: `shift()` é O(n) e a fila chega a milhares de tiles.
  const queue: number[] = [keyOf(startX, startY)];
  let head = 0;
  let expanded = 0;

  while (head < queue.length) {
    const current = queue[head++]!;
    if (++expanded > maxNodes) return null;

    const cx = Math.floor(current / 0x10000);
    const cy = current % 0x10000;

    for (const direction of CARDINALS) {
      const vector = DIRECTION_VECTORS[direction];
      const nx = cx + vector.dx;
      const ny = cy + vector.dy;

      // Fora do mapa é recusado pelo próprio `isFree` — `Terrain.at` devolve parede fora dos
      // limites —, então não há checagem de borda a duplicar aqui.
      const next = keyOf(nx, ny);
      if (cameFrom.has(next)) continue;
      if (!isFree(nx, ny)) continue;

      cameFrom.set(next, { key: current, direction });

      if (reached(nx, ny)) return reconstruct(cameFrom, next);

      queue.push(next);
    }
  }

  return null;
}

/** Norte, leste, sul e oeste — a vizinhança que o `Direction` do protocolo alcança. */
const CARDINALS: readonly Direction[] = [
  Direction.North,
  Direction.East,
  Direction.South,
  Direction.West,
];

function reconstruct(
  cameFrom: Map<number, { key: number; direction: Direction } | null>,
  goal: number,
): Direction[] {
  const steps: Direction[] = [];

  for (let current = goal; ; ) {
    const from = cameFrom.get(current);
    if (!from) break;

    steps.push(from.direction);
    current = from.key;
  }

  steps.reverse();
  return steps;
}

/**
 * A caminhada em curso: o que falta andar e para onde.
 *
 * Vive entre dois quadros porque um caminho leva segundos para ser percorrido — cada passo
 * custa a trava de `Predictor.cooldownMs` — e no meio disso o mundo mexe: uma criatura entra
 * no corredor, uma porta se fecha, o servidor corrige a posição. As três coisas são tratadas
 * do mesmo jeito, replanejando a partir de onde o personagem **está** para o destino que o
 * jogador pediu. O destino é a intenção; o caminho é só a estimativa corrente de como
 * chegar lá.
 */
export class WalkPath {
  private steps: Direction[] = [];
  private cursor = 0;

  private goalX = 0;
  private goalY = 0;

  /** Onde o personagem deveria estar antes do próximo passo. Ver `next`. */
  private expectedX = 0;
  private expectedY = 0;

  get active(): boolean {
    return this.cursor < this.steps.length;
  }

  /** O tile pedido, ou `null` quando não há caminhada em curso. */
  get goal(): { x: number; y: number } | null {
    return this.active ? { x: this.goalX, y: this.goalY } : null;
  }

  /**
   * Traça um caminho novo. `false` quando não há como chegar — e nesse caso nada fica
   * pendurado, porque um destino guardado sem caminho voltaria a ser tentado a cada passo.
   */
  to(
    fromX: number,
    fromY: number,
    goalX: number,
    goalY: number,
    isFree: (x: number, y: number) => boolean,
  ): boolean {
    const steps = findPath(fromX, fromY, goalX, goalY, isFree);
    if (steps === null || steps.length === 0) {
      this.clear();
      return steps !== null;
    }

    this.steps = steps;
    this.cursor = 0;
    this.goalX = goalX;
    this.goalY = goalY;
    this.expectedX = fromX;
    this.expectedY = fromY;
    return true;
  }

  clear(): void {
    this.steps.length = 0;
    this.cursor = 0;
  }

  /**
   * A direção do próximo passo, ou `null` quando a caminhada acabou ou morreu.
   *
   * Replaneja **uma vez** por chamada quando o personagem não está onde o caminho supunha,
   * ou quando o passo seguinte deixou de caber. Uma só, e não em laço: o replanejamento sai
   * de `findPath`, que só expande tile livre, então o primeiro passo do caminho novo cabe por
   * construção. Se nem o caminho novo existe, a caminhada morre em silêncio — quem clicou já
   * viu o personagem parar, e uma frase por criatura que cruza um corredor viraria ruído.
   */
  next(
    fromX: number,
    fromY: number,
    isFree: (x: number, y: number) => boolean,
  ): Direction | null {
    if (!this.active) return null;

    const drifted = fromX !== this.expectedX || fromY !== this.expectedY;
    const direction = this.steps[this.cursor]!;
    const vector = DIRECTION_VECTORS[direction];
    const blocked = !isFree(fromX + vector.dx, fromY + vector.dy);

    if (drifted || blocked) {
      const goalX = this.goalX;
      const goalY = this.goalY;
      if (!this.to(fromX, fromY, goalX, goalY, isFree) || !this.active) return null;

      return this.take(fromX, fromY);
    }

    return this.take(fromX, fromY);
  }

  private take(fromX: number, fromY: number): Direction {
    const direction = this.steps[this.cursor]!;
    const vector = DIRECTION_VECTORS[direction];

    this.cursor += 1;
    this.expectedX = fromX + vector.dx;
    this.expectedY = fromY + vector.dy;

    return direction;
  }
}
